import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * DTO interfaces for Certificate Micro-Publish API
 */

export interface OverwriteStatus {
  overwritten: boolean;
  overwrittenDate?: Date;
  overwrittenBy?: string;
  backupPath?: string;
}

export interface CertificatePublishHistoryDto {
  path: string;
  publishDate: Date;
  publishedBy: string;
  publishedByDisplayName: string;
  publishType: 'full' | 'micro';
  overwriteStatus: OverwriteStatus;
}

export interface CertificatePublishHistoryResponse {
  content: CertificatePublishHistoryDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface CertificateMicroPublishRequest {
  selectedPaths: string[];
}

export interface OverwrittenPath {
  originalPath: string;
  backupPath: string;
}

export interface CertificateMicroPublishResult {
  success: boolean;
  publishId?: string;
  totalPathsProcessed: number;
  successfulOverwrites: number;
  overwrittenPaths?: OverwrittenPath[];
  errorMessage?: string;
}

/**
 * Service for Certificate Micro-Publish operations.
 * Provides methods to retrieve publish history and execute micro-publish operations.
 */
@Injectable({
  providedIn: 'root'
})
export class CertificateMicroPublishService {
  private baseUrl = `${environment.BASE_URL}micro-publish/certificates`;

  constructor(private http: HttpClient) {}

  /**
   * Retrieves the publish history for a specific certificate with pagination.
   *
   * @param certificateId - The ID of the certificate
   * @param page - Page number (0-indexed, default: 0)
   * @param size - Page size (default: 20)
   * @returns Observable of paginated publish history
   */
  getPublishHistory(
    certificateId: string,
    page: number = 0,
    size: number = 20
  ): Observable<CertificatePublishHistoryResponse> {
    return this.http.get<CertificatePublishHistoryResponse>(
      `${this.baseUrl}/${certificateId}/history`,
      { params: { page: page.toString(), size: size.toString() } }
    );
  }

  /**
   * Executes a micro-publish operation for selected certificate paths.
   *
   * @param certificateId - The ID of the certificate to micro-publish
   * @param request - The request containing selected paths to overwrite
   * @returns Observable of micro-publish result
   */
  executeMicroPublish(
    certificateId: string,
    request: CertificateMicroPublishRequest
  ): Observable<CertificateMicroPublishResult> {
    return this.http.post<CertificateMicroPublishResult>(
      `${this.baseUrl}/${certificateId}`,
      request
    );
  }
}
